<script setup lang="ts">
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import CalendarFontField from './CalendarFontField.vue';
import { loadCustomMarker, normalizeCommaList } from './calendarUtils';
import { useCalendarConfig } from './useCalendarConfig';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig } from './types';

// Vue 2.7 SFC compiler cannot resolve imported types in defineProps
const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
}>();
const emit = defineEmits<{
    (e: 'update-config', config: Partial<CalendarConfig>): void;
}>();

const { config, elementKey, disabled, update } = useCalendarConfig(props, emit);

const onDayLettersChange = (value: string | number = '') => {
    const dayLetters = normalizeCommaList(String(value));
    if (dayLetters.split(',').length !== 7) return;
    update({ dayLetters });
};

const onDayFontSizeChange = async (value: string | number = 0) => {
    const dayFontSize = String(value);
    // The marker is picked in another section, so the element holds the current one, not the local copy
    const { markerType, customMarkerPath, customMarkerScale } = props.imageElement?.calendarConfig ?? {};
    if (markerType !== 'custom' || !customMarkerPath) {
        update({ dayFontSize });
        return;
    }

    try {
        // Uploaded markers are sized when built, so they are rebuilt to keep up with the day numbers
        update({ dayFontSize, ...(await loadCustomMarker(customMarkerPath, dayFontSize, customMarkerScale)) });
    } catch (error) {
        console.error('The custom marker could not be rescaled', error);
        update({ dayFontSize });
    }
};
</script>

<template>
    <div v-if="config" :key="elementKey" class="calendar-section">
        <div class="row">
            <c-input
                class="col-6"
                :value="Number(config.daysTopMargin)"
                type="decimal"
                step=".1"
                :min="0"
                label="Top Margin"
                :disabled="disabled"
                @input="update({ daysTopMargin: String($event) })"
            />
            <c-input
                class="col-6"
                :value="Number(config.daysBottomMargin)"
                type="decimal"
                step=".1"
                :min="0"
                label="Bottom Margin"
                :disabled="disabled"
                @input="update({ daysBottomMargin: String($event) })"
            />
        </div>
        <div class="d-flex flex-wrap justify-content-between">
            <c-checkbox
                :value="config.showHorizontalLine"
                label="Show Days Separator Line"
                :disabled="disabled"
                @input="update({ showHorizontalLine: $event })"
            />
            <c-checkbox
                :value="config.sundayFirst"
                label="Sunday First"
                :disabled="disabled"
                @input="update({ sundayFirst: $event })"
            />
            <c-checkbox
                :value="config.showDaysTitles"
                label="Show Day Titles"
                :disabled="disabled"
                @input="update({ showDaysTitles: $event })"
            />
        </div>

        <calendar-color-field
            v-if="config.showHorizontalLine"
            title="Day Separator Line"
            :value="config.lineColor"
            :disabled="disabled"
            @input="update({ lineColor: $event })"
        />

        <template v-if="config.showDaysTitles">
            <calendar-field-title>Day Titles</calendar-field-title>
            <div class="row">
                <c-input
                    class="col-6"
                    :value="config.dayLetters.split(',').join(', ')"
                    type="text"
                    label="Day Titles"
                    placeholder="S, M, T, W, T, F, S"
                    :disabled="disabled"
                    @input="onDayLettersChange"
                />
                <c-input
                    class="col-6"
                    :value="Number(config.daysTitlesFontSize)"
                    type="decimal"
                    step=".1"
                    :min="0"
                    label="Font size (pts)"
                    :disabled="disabled"
                    @input="update({ daysTitlesFontSize: String($event) })"
                />
            </div>
            <calendar-font-field
                :value="config.daysTitlesFontPath"
                :disabled="disabled"
                @input="update({ daysTitlesFontPath: $event })"
            />
            <calendar-color-field
                :value="config.daysTitlesColor"
                :disabled="disabled"
                @input="update({ daysTitlesColor: $event })"
            />
        </template>

        <calendar-field-title>Days</calendar-field-title>
        <c-input
            data-testid="calendar-day-font-size"
            :value="Number(config.dayFontSize)"
            type="decimal"
            step=".1"
            :min="0"
            label="Font size (pts)"
            :disabled="disabled"
            @input="onDayFontSizeChange"
        />
        <calendar-font-field
            :value="config.dayFontPath"
            :disabled="disabled"
            @input="update({ dayFontPath: $event })"
        />
        <calendar-color-field :value="config.dayColor" :disabled="disabled" @input="update({ dayColor: $event })" />
    </div>
</template>

<style scoped lang="scss">
@import './calendarSection';
</style>
